import React from 'react';
import styled from 'styled-components';
import DrawerBody from 'components/ui/DrawerBody';
import DrawerHeader from 'components/ui/DrawerHeader';
import DrawerFooter from 'components/ui/DrawerFooter';
import Spacer from 'components/ui/Spacer';

import CreateProspectDrawerDetailsStep from './CreateProspectDrawerDetailsStep';
import AddProspectNote from './AddProspectNote';
import CtaPrimary from 'components/ui/CtaPrimary';
import FadeInUpAnimation from 'components/ui/FadeInUpAnimation';

import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Menu from 'components/ui/Menu';
import Dropdown from 'components/ui/Dropdown';
import DrawerTabs from 'components/ui/DrawerTabs';
import GroupedButtons from 'components/ui/GroupedButtons';
import StatusButton from 'components/ui/StatusButton';
import DrawerSuccessMessages from 'components/ui/DrawerSuccessMessages';
import Divider from 'components/ui/Divider';
import CardWithIconHeader from 'components/ui/CardWithIconHeader';

import Icon, { CheckOutlined } from '@ant-design/icons';
import { Typography } from 'antd';

import errorAnimationData from 'assets/error.json';
import loadingAnimationData from 'assets/loading.json';
import successAnimationData from 'assets/success.json';
import { apiPut } from 'helpers/ApiHelper';
import Auth from 'j-toker';
import Lottie from 'react-lottie';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import { toFriendlyDateTimeString } from 'helpers/Formatter';
import groupSvg from 'assets/group-2.svg';
import { PROSPECTS_STATUS } from 'helpers/StatusHelper';

const { TabPane } = DrawerTabs;
const { Title } = Typography;

const FORM_NAME = 'prospects_details_form';

const StyledImgContainer = styled.div`
  display: flex;
  position: relative;
  top: 80px;
  left: 50%;
  transform: translateX(-50%);
  flex-direction: column;
  align-items: center;
  margin-bottom: 150px;
`;

const StyledIllustration = styled.img`
  width: 80%;
  max-width: 576px;
  margin-bottom: 24px;
`;

const StyledTextNoMatch = styled.p`
  width: 100%;
  max-width: 269px;
  font-family: 'Circular-Bold', 'Circluar', sans-serif;
  font-size: 16px;
  text-align: center;
`;

const StyledCol = styled(Col)`
  margin: 8px 0;
`;

const StyledDropdownContainer = styled.div`
  display: inline-flex;
  vertical-align: top;
  margin-left: 12px !important;
  text-transform: capitalize;
`;

const StyledTabPane = styled(TabPane)`
  height: 100% !important;
`;

const CardTitle = styled.h5`
  margin: 8px 0;
  text-transform: capitalize;
  padding: 0;
  font-size: 18px;
`;

class ProspectDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      loading: false,
      success: false,
      formData: {},
      tabIndex: '1',
    };
  }

  handleLoadingChange = (options) => {
    this.setState({
      loading: options.isLoading,
      error: options.isError,
      formData: options.values,
    });
  };

  handleSubmissionComplete = async (formData) => {
    this.setState({
      error: false,
      loading: false,
      success: true,
      formData: formData,
    });

    this.props.getProspect();
    this.props.fetchProspectAndTableData();
  };

  changeTab = (key) => {
    this.setState({ tabIndex: key });
  };

  render() {
    if (this.state.success) {
      return this.renderSuccess(this.state.formData);
    } else if (this.state.loading) {
      return this.renderLoading(this.state.formData);
    } else if (this.state.error) {
      return this.renderError(this.state.formData);
    } else {
      return this.renderFormWizard();
    }
  }

  renderProspectStatus = () => {
    const isWorking = [
      PROSPECTS_STATUS.IN_CONTACT,
      PROSPECTS_STATUS.APPLYING,
      PROSPECTS_STATUS.BOOKED_SHOWING,
      PROSPECTS_STATUS.STALE,
      PROSPECTS_STATUS.WAITLISTED,
    ].includes(this.props.prospect.status);

    const isConverted = [PROSPECTS_STATUS.CONVERTED, PROSPECTS_STATUS.CLOSED].includes(
      this.props.prospect.status
    );

    return (
      <Row gutter={16} style={{ backgroundColor: '#fff', padding: '0 24px' }}>
        <StyledCol xs={24} sm={12}>
          <GroupedButtons groupStyle="left" onClick={() => this.updateProspectStatus(8)}>
            {getTranslatedString('prospects.new')}
            {!isWorking && !isConverted && <Icon component={CheckOutlined} />}
          </GroupedButtons>
          <GroupedButtons
            groupStyle="middle"
            onClick={() => this.updateProspectStatus(0)}
            active={isWorking || isConverted}
            disabled={isWorking}
          >
            {getTranslatedString('prospects.working')}
            {isWorking && !isConverted && <Icon component={CheckOutlined} />}
          </GroupedButtons>
          <GroupedButtons
            groupStyle="right"
            onClick={() => this.updateProspectStatus(3)}
            active={isConverted}
            disabled={isConverted}
          >
            {getTranslatedString('prospects.converted')}
            {isConverted && <Icon component={CheckOutlined} />}
          </GroupedButtons>
        </StyledCol>
        <StyledCol xs={24} sm={12}>
          <StatusButton
            active={this.props.prospect.status === 5}
            onClick={() => this.updateProspectStatus(5)}
          >
            {getTranslatedString('prospects.stale')}
          </StatusButton>
          <StatusButton
            active={this.props.prospect.status === 6}
            onClick={() => this.updateProspectStatus(6)}
          >
            {getTranslatedString('prospects.waitlist')}
          </StatusButton>
          <StatusButton
            active={this.props.prospect.status === 2}
            onClick={() => this.updateProspectStatus(2)}
          >
            {getTranslatedString('prospects.reject')}
          </StatusButton>
        </StyledCol>
      </Row>
    );
  };

  updateProspectStatus = (status) => {
    const payload = {
      ...this.props.prospect,
      status,
    };

    this.updateProspect(payload);
  };

  updateProspectTemperature = (temperature) => {
    let payload = {
      ...this.props.prospect,
      temperature,
    };

    if (temperature === '-') {
      delete payload.temperature;
    }

    this.updateProspect(payload);
  };

  updateProspect = (payload) => {
    this.setState({ loading: true, formData: payload, error: false }, () =>
      apiPut(Auth.getApiUrl() + `/landlords/lead_cards/${this.props.prospect.id}`, payload)
        .done((response) => {
          this.props.fetchProspectAndTableData();
          this.setState({ loading: false, success: true });
        })
        .catch((error) => {
          this.setState({ loading: false, error: true });
        })
    );
  };

  renderProspectTemperatureMenu = () => (
    <Menu onClick={({ key }) => this.updateProspectTemperature(key)}>
      <Menu.Item key="hot">{getTranslatedString('prospects.hot')}</Menu.Item>
      <Menu.Item key="warm">{getTranslatedString('prospects.warm')}</Menu.Item>
      <Menu.Item key="cold">{getTranslatedString('prospects.cold')}</Menu.Item>
      <Menu.Item key="-">
        {getTranslatedString('landlordPortal.amenities.time_unit.none')}
      </Menu.Item>
    </Menu>
  );

  renderDrawerHeader = () => (
    <React.Fragment>
      {`${this.props.prospect.first_name} ${this.props.prospect.last_name}`}
      <Dropdown overlay={this.renderProspectTemperatureMenu()}>
        <StyledDropdownContainer>
          <StatusButton>
            {this.props.prospect.temperature}
            <Icon type="caret-down" style={{ verticalAlign: 'sub' }} />
          </StatusButton>
        </StyledDropdownContainer>
      </Dropdown>
    </React.Fragment>
  );

  renderEventTitle = (event) => {
    return (
      <React.Fragment>
        <div style={{ display: 'inline-grid', verticalAlign: 'top', padding: 0 }}>
          <CardTitle>{event.event_type}</CardTitle>
        </div>
      </React.Fragment>
    );
  };

  renderFormWizard = () => {
    const { loading, error, tabIndex, formData } = this.state;

    const isConverted = [PROSPECTS_STATUS.CONVERTED, PROSPECTS_STATUS.CLOSED].includes(
      this.props.prospect.status
    );

    if (!this.props.prospect.first_name) return null;

    return (
      <React.Fragment>
        <DrawerHeader
          onClose={this.props.handleClose}
          title={this.renderDrawerHeader()}
          tabbedHeaderStyle
        />
        <DrawerBody tabbedDrawerBodyStyle>
          {this.renderProspectStatus()}
          <DrawerTabs center defaultActiveKey="1" onChange={this.changeTab}>
            <StyledTabPane tab="Activity" key="1">
              {this.renderProspectHistory()}
            </StyledTabPane>
            <StyledTabPane tab={getTranslatedString('prospects.prospectDetails')} key="2">
              {!error && !loading ? this.renderDetailsStep(formData) : null}
            </StyledTabPane>
          </DrawerTabs>
        </DrawerBody>
        {tabIndex === '2' && (
          <FadeInUpAnimation>
            {!isConverted && (
              <DrawerFooter
                primaryActionFormName={FORM_NAME}
                primaryActionText={getTranslatedString('update')}
                primaryActionDisabled={loading}
              />
            )}
          </FadeInUpAnimation>
        )}
      </React.Fragment>
    );
  };

  renderProspectHistory = () => (
    <div style={{ height: '100%', overflowY: 'scroll', paddingBottom: '160px' }}>
      <Spacer mb={2} />
      <Row gutter={16} style={{ padding: '0 24px' }}>
        <AddProspectNote
          formName="ADD_PROSPECT_NOTES"
          prospect={this.props.prospect}
          onLoadingChange={this.handleLoadingChange}
          onSubmissionComplete={this.handleSubmissionComplete}
        />
      </Row>
      <Spacer mb={2} />
      <Row gutter={16} style={{ padding: '0 36px' }}>
        <Title level={3} style={{ margin: '0 0 12px' }}>
          {getTranslatedString('prospects.activityFeed')}
        </Title>
      </Row>
      <Row gutter={16} style={{ padding: '0 36px' }}>
        {this.props.prospect.events.length > 0 && this.renderProspectEvents()}
        {this.props.prospect.events.length === 0 && this.renderNoProspectEvents()}
      </Row>
    </div>
  );

  renderProspectEvents = () => (
    <React.Fragment>
      {this.props.prospect.events
        .sort((a, b) => (a.time > b.time ? -1 : 1))
        .map((event) => (
          <React.Fragment>
            <CardWithIconHeader title={this.renderEventTitle(event)}>
              <Divider style={{ margin: '-32px 0 16px 0' }} />
              <p style={{ margin: 0, color: '#85868c', fontSize: '10px' }}>
                {toFriendlyDateTimeString(event.time, { language: getCurrentLocale() })}
              </p>
              <p style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 800 }}>
                {event.title}
              </p>
              <p style={{ marginBottom: 0 }}>{event.verbiage}</p>
            </CardWithIconHeader>
            <Spacer mb={2} />
          </React.Fragment>
        ))}
    </React.Fragment>
  );

  renderNoProspectEvents = () => (
    <StyledImgContainer>
      <StyledIllustration src={groupSvg} alt="no results found" />
      <StyledTextNoMatch>
        {getTranslatedString('prospects.hmmNoEvents', {
          prospectName: `${this.props.prospect.first_name} ${this.props.prospect.last_name}`,
        })}
      </StyledTextNoMatch>
    </StyledImgContainer>
  );

  renderSuccess = (formData) => {
    const defaultOptions = {
      loop: false,
      autoplay: true,
      animationData: successAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('addProspectDrawer.successHeader', {
          first_name: formData.first_name,
          last_name: formData.last_name,
        })}
        bodyText={getTranslatedString('globalDrawer.successBody')}
        cta={
          <CtaPrimary onClick={() => this.setState({ success: false })}>
            {getTranslatedString('close')}
          </CtaPrimary>
        }
      />
    );
  };

  renderError = (formData) => {
    const defaultOptions = {
      loop: false,
      autoplay: true,
      animationData: errorAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('globalDrawer.errorHeader', {
          name: `${formData.first_name} ${formData.last_name}`,
        })}
        bodyText={getTranslatedString('globalDrawer.errorBody')}
        cta={
          <CtaPrimary onClick={() => this.setState({ error: false })}>
            {getTranslatedString('globalDrawer.goBack')}
          </CtaPrimary>
        }
      />
    );
  };

  renderLoading = (formData) => {
    const defaultOptions = {
      loop: true,
      autoplay: true,
      animationData: loadingAnimationData,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid slice',
      },
    };

    return (
      <DrawerSuccessMessages
        headerImageSource={<Lottie options={defaultOptions} height={150} width={150} />}
        headerText={getTranslatedString('globalDrawer.loadingHeader', {
          object: getTranslatedString('prospect'),
        })}
        bodyText={getTranslatedString('globalDrawer.loadingBody')}
      />
    );
  };

  renderDetailsStep = () => {
    return (
      <div style={{ padding: '24px', overflowY: 'scroll', height: 'calc(100% - 135px)' }}>
        <CreateProspectDrawerDetailsStep
          trafficSources={this.props.trafficSources}
          formName={FORM_NAME}
          formData={this.props.prospect}
          onLoadingChange={this.handleLoadingChange}
          onSubmissionComplete={this.handleSubmissionComplete}
          properties={this.props.properties}
          unitTypes={this.props.unitTypes}
          isEditing
        />
      </div>
    );
  };
}

export default ProspectDetails;
